/**
 * Shared reservation status colours.
 * `text` = brand / accent (chips, dots, labels)
 * `bg` = contrasting surface for badges & chip text
 */
export const STATUS_COLOURS = {
  Seated: { bg: "#E6F2EC", text: "#095330" },
  Completed: { bg: "#E7F5EC", text: "#0D7F3E" },
  Approved: { bg: "#E8EDFA", text: "#0735B6" },
  Requested: { bg: "#0B2A4A", text: "#7EB4F7" },
  Cancelled: { bg: "#FFECEC", text: "#bd3e3e" },
  Rejected: { bg: "#EEF1F4", text: "#38485D" },
  Modified: { bg: "#252525", text: "#FABA07" },
};

export const STATUS_LIST = Object.keys(STATUS_COLOURS);

export const DEFAULT_STATUS_COLOUR = STATUS_COLOURS.Requested;

/** Resolve colour for any status label (incl. Expired → Cancelled) */
export function getStatusColour(status) {
  if (!status) return DEFAULT_STATUS_COLOUR;
  if (status === "Expired") return STATUS_COLOURS.Cancelled;
  if (STATUS_COLOURS[status]) return STATUS_COLOURS[status];
  const key = STATUS_LIST.find(
    (k) => k.toLowerCase() === String(status).toLowerCase()
  );
  return (key && STATUS_COLOURS[key]) || DEFAULT_STATUS_COLOUR;
}

/**
 * True when booking datetime is in the past (or status is Expired).
 * Supports bookingTime as "YYYY-MM-DD HH:mm:ss" or separate bookingDate + bookingTime.
 */
export function isReservationExpired(enquiry) {
  if (!enquiry) return false;
  if (enquiry.status === "Expired") return true;

  const bookingAt = enquiry.bookingTime || enquiry.bookingDate;
  if (!bookingAt) return false;

  const raw = String(bookingAt).trim();
  // Full datetime from list APIs
  if (raw.includes(" ")) {
    const booked = new Date(raw.replace(" ", "T"));
    if (Number.isNaN(booked.getTime())) return false;
    return booked < new Date();
  }

  // Date-only (+ optional time string like ViewReservation)
  const bookedDate = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw
  );
  if (Number.isNaN(bookedDate.getTime())) return false;

  const today = new Date();
  const bookedDateOnly = new Date(
    bookedDate.getFullYear(),
    bookedDate.getMonth(),
    bookedDate.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  if (bookedDateOnly < todayOnly) return true;

  if (bookedDateOnly.getTime() === todayOnly.getTime() && enquiry.bookingTime) {
    const timeOnly = String(enquiry.bookingTime);
    if (/^\d{2}:\d{2}/.test(timeOnly)) {
      const hours = String(today.getHours()).padStart(2, "0");
      const minutes = String(today.getMinutes()).padStart(2, "0");
      const seconds = String(today.getSeconds()).padStart(2, "0");
      return timeOnly < `${hours}:${minutes}:${seconds}`;
    }
  }

  return false;
}
